import { Box, CircularProgress, LinearProgress, Stack } from '@mui/material';

import SuiTypography from '@upheal/design-system/components/legacy/Sui/SuiTypography';

import { useQuery } from '@apollo/client';
import { GetPlanUpgradeDocument } from '@upheal/graphql/definitions/billing/GetPlanUpgrade.generated';
import { captureException } from '@upheal/shared/utils/sentry';
import BillingPeriodSwitch from '@upheal/billing/BillingPeriodSwitch/BillingPeriodSwitch';
import { useSnackbar } from '@upheal/design-system/vendor/notistack';
import { useMemo, useRef, useState } from 'react';
import { GQLPlanUpgradeFragment } from '@upheal/graphql/definitions/schema.generated';
import { trackUiEvent } from '../../../utils/analytics/ui-tracking';
import { useEvent } from 'react-use';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { usePlanChangePortal } from '@upheal/billing/hooks/usePlanChangePortal';
import {
    SubscriptionFeatureId,
    useCustomerDetails,
} from '../../../state/useCustomerDetails';
import { usePermissions } from '../../../state/usePermissions';
import { PricingCard } from './PricingCard/PricingCard';
import {
    isGroupPlan,
    isMeteredPlan,
    isPaidPlan,
    parsePlanId,
} from '@upheal/billing/utils/plans';
import { Modal } from '@upheal/design-system/components/Modal/Modal';
import { Typography } from '@upheal/design-system/components/Typography/Typography';
import { Button } from '@upheal/design-system/components/Button/Button';
import { usePricingUrl } from '@upheal/billing/hooks/usePricingUrl';
import { MeteredPlanUpgrade } from './MeteredPlanUpgrade/MeteredPlanUpgrade';

type PlanUpgradeProps = {
    featureId: SubscriptionFeatureId;
    onClose: () => void;
    zIndex?: number;
};

const PlanUpgrade = (props: PlanUpgradeProps) => {
    const { featureId, zIndex, onClose } = props;
    const { enqueueSnackbar } = useSnackbar();

    const { data, loading: loadingPlanUpgrade } = useQuery(
        GetPlanUpgradeDocument,
        {
            variables: { featureId },
            notifyOnNetworkStatusChange: true,
            onError: (error) => {
                captureException('Failed to load plan upgrade data', {
                    data: error,
                });
                enqueueSnackbar(
                    'Something went wrong. We are looking into that.',
                    {
                        variant: 'error',
                    },
                );
                onClose();
            },
        },
    );

    return (
        <Modal
            id="plan-upgrade-modal"
            open
            sx={{ zIndex }}
            maxWidth={832}
            onClose={onClose}
            sizing="md"
        >
            {loadingPlanUpgrade && (
                <Modal.Content sx={{ py: 8 }}>
                    <LinearProgress />
                </Modal.Content>
            )}
            {data?.getPlanUpgrade && (
                <PlanUpgradeContent
                    {...props}
                    planUpgradeData={data.getPlanUpgrade}
                />
            )}
        </Modal>
    );
};

type PlanUpgradeContentProps = {
    planUpgradeData: GQLPlanUpgradeFragment;
};

const PlanUpgradeContent = (
    props: PlanUpgradeProps & PlanUpgradeContentProps,
) => {
    const { onClose, planUpgradeData } = props;
    const [yearlyChecked, setYearlyChecked] = useState(true);
    const { createPlanChangeSession } = usePlanChangePortal();
    const { enqueueSnackbar } = useSnackbar();
    const { loadingCustomerDetails, refetchCustomerDetails } =
        useCustomerDetails();
    const { canPurchasePlan } = usePermissions();
    const { plansComparisonUrl } = usePricingUrl();
    const isPaygEnabled = useFeatureFlagEnabled('growth-payg-pricing');
    const newPlanIdRef = useRef<string | null>(null);
    const { showPeriodSwitch, options, currentSubscription } = planUpgradeData;

    const showMeteredBanner =
        isPaygEnabled && options.length === 1 && isMeteredPlan(options[0].id);

    const refreshSubscription = async () => {
        try {
            const { data } = await refetchCustomerDetails();

            if (
                data?.getCustomerDetails?.subscription?.planId ===
                newPlanIdRef.current
            ) {
                enqueueSnackbar('Plan upgrade successful!', {
                    variant: 'success',
                });
                onClose();
            }
            // else: the plan upgrade didn't happened, or user just switched tabs
        } catch (error) {
            captureException('[billing] Failed to refresh customer details.', {
                error,
            });
            // not reporting a snackbar, as we don't know whether the refresh failed due
            // to a network error or the plan upgrade itself.
            // Keeping the dialog open so the user can try again or close it
        }
    };

    useEvent('visibilitychange', () => {
        if (document.visibilityState === 'visible' && newPlanIdRef.current) {
            void refreshSubscription();
        }
    });

    const handleCheckout = async (newPlanId: string) => {
        trackUiEvent('Click', 'Upgrade Plan', 'Upgrade Plan Dialog');

        if (!canPurchasePlan) {
            enqueueSnackbar(
                'Please contact your administrator to choose a plan that includes this feature.',
                {
                    variant: 'info',
                },
            );
            return;
        }

        newPlanIdRef.current = newPlanId;

        const portalUrl = await createPlanChangeSession(newPlanId);

        if (portalUrl) {
            window.open(portalUrl, '_self', 'noopener,noreferrer');
        }
    };

    const availableOnTitle = useMemo(() => {
        const paidPlanIds = ['starter', 'premium', 'unlimited'];
        const applicableOptions = options.filter(
            (option) =>
                !(
                    option.id.startsWith('group') ||
                    option.id.startsWith('enterprise')
                ),
        );
        const availablePlanIds = applicableOptions.map((option) => option.id);
        const allPaidPlansAvailable = paidPlanIds.every((planId) =>
            availablePlanIds.some((availablePlanId) =>
                availablePlanId.includes(planId),
            ),
        );

        if (allPaidPlansAvailable) {
            return 'all paid plans';
        }

        return Array.from(
            new Set(applicableOptions.map((option) => option.name)),
        ).join(' or ');
    }, [options]);

    const optionsToShow = options.filter((option) => {
        const isYearlyPlan = option.id.endsWith('-yearly');
        return yearlyChecked ? isYearlyPlan : !isYearlyPlan;
    });

    const { pricingTier: currentPricingTier, isYearly: isCurrentPlanYearly } =
        parsePlanId(currentSubscription.planId);

    if (isGroupPlan(currentPricingTier)) {
        enqueueSnackbar('Unknown error occurred. We are looking into it.', {
            variant: 'error',
        });
        captureException(
            '[billing] Unexpectedly showing plan upgrade dialog for group plan',
        );

        return;
    }

    if (showMeteredBanner) {
        return (
            <MeteredPlanUpgrade planName={options[0].name} onClose={onClose} />
        );
    }

    return (
        <>
            <Modal.Header
                title={`Available on ${availableOnTitle}`}
                onClose={onClose}
            />

            <Modal.Content sx={{ position: 'relative' }} scrollable>
                {loadingCustomerDetails && (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        bgcolor="rgba(255, 255, 255, 0.7)"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        zIndex={1}
                    >
                        <CircularProgress />
                    </Box>
                )}
                <Stack direction="row" spacing={2} justifyContent="center">
                    {isPaidPlan(currentSubscription.planId) && (
                        <Stack
                            alignItems="center"
                            gap={1.5}
                            pt={showPeriodSwitch ? 4.5 : 0}
                        >
                            <SuiTypography
                                variant="body2"
                                color="secondary"
                                fontWeight="bold"
                            >
                                Your current plan
                            </SuiTypography>
                            <PricingCard
                                tier={currentPricingTier}
                                yearlyPricing={isCurrentPlanYearly}
                                showDiscountInfo={false}
                                forceHideEmptyYearlySavingsArea={
                                    !showPeriodSwitch
                                }
                                ctaPlacement="none"
                                isDimmed={true}
                                flexGrow={1}
                            />
                        </Stack>
                    )}
                    {/* Plans available for upgrade */}
                    <Stack
                        alignItems="center"
                        pt={showPeriodSwitch ? 1 : 3.5}
                        spacing={1.5}
                    >
                        {showPeriodSwitch && (
                            <BillingPeriodSwitch
                                yearlyChecked={yearlyChecked}
                                onYearlyCheckedChange={setYearlyChecked}
                            />
                        )}
                        <Stack direction="row" flexWrap="wrap" gap={2}>
                            {optionsToShow.map((option) => {
                                const { pricingTier, isYearly } = parsePlanId(
                                    option.id,
                                );
                                if (pricingTier === 'group') {
                                    captureException(
                                        '[billing] Unexpectedly returned group plan in plan upgrade options',
                                    );
                                    return null;
                                }

                                return (
                                    <PricingCard
                                        key={option.id}
                                        tier={pricingTier}
                                        yearlyPricing={isYearly}
                                        ctaPlacement={
                                            currentSubscription.planId == 'free'
                                                ? 'middle'
                                                : 'bottom'
                                        }
                                        showDiscountInfo={showPeriodSwitch}
                                        forceHideEmptyYearlySavingsArea={
                                            !showPeriodSwitch
                                        }
                                        onClick={() =>
                                            handleCheckout(option.id)
                                        }
                                        flexGrow={1}
                                        uiLocation="Upgrade Plan Dialog"
                                    />
                                );
                            })}
                        </Stack>
                    </Stack>
                </Stack>
                <Stack alignItems="center" pt={3} spacing={2}>
                    <Typography>
                        Not sure which plan is best for you?
                    </Typography>

                    <Button
                        variant="link"
                        href={plansComparisonUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() =>
                            trackUiEvent(
                                'Click',
                                'Compare Plans',
                                'Upgrade Plan Dialog',
                            )
                        }
                    >
                        See full feature comparison
                    </Button>
                </Stack>
            </Modal.Content>
        </>
    );
};

export default PlanUpgrade;
